import {AnalyticsEventMetadata} from 'analytics/AnalyticsTypes';
import {ArchiveRecord} from 'archive/ArchiveConstants';
import {DataModificationsHeader} from 'audit/DataModifications';
import {SchemaCollaborator} from 'auth/PermissionConstants';
import {
  ClientTagSchemaConstraint,
  RegistryLabelingStrategy,
  MixtureSchemaConfig,
  TagSchema,
} from 'constants/RegistryConstants';
import {TagSchemaItemType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {AnalyticsDashboardLinkParentSpecRequest} from 'insights/embeddings/RESTEmbeddingTypes';
import {NameTemplate, NameTemplatePart} from 'name-templates/NameTemplateConstants';
import {stripExcessKeys} from 'name-templates/NameTemplateUtil';
import {ClientSchemaField} from 'registry/field/FieldTable';
import {sanitizeFields} from 'registry/RegistryUtil';
import {SchemaField, SchemaFieldEdge, SchemaFieldPath} from 'schemas/fields/SchemaFieldConstants';
import {ImplementedStaticSchemaInterface} from 'schemas/SchemaConstants';
import {StarsResponse} from 'stars/StarsConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import * as net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';
import {
  AdvancedRuleParams,
  UpdateValidationRulesParams,
  ValidationRuleUpsertParams,
} from 'validation_rules/network/types';

import {CollaboratorInfoGraphQL} from '../../graphql-fragments/auth/__generated/CollaboratorInfoGraphQL';
import {AnalyticsDashboardLinkParentGraphQL} from '../../graphql-fragments/insights/__generated/AnalyticsDashboardLinkParentGraphQL';
import {TagSchemaGraphQL} from '../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaGraphQL';

export function delete_(tagSchemaId: string): Promise<void> {
  return net.deletePromise({url: `/1/api/tag-schemas/${tagSchemaId}`});
}

export interface UpdateSchemaCollaboratorsRequest {
  collaborators: Array<SchemaCollaborator>;
}

export interface BulkArchiveTagSchemasResponse {
  archivedItemIds: Array<string>;
  archiveRecordsByItemId: {[id: string]: ArchiveRecord};
}

export function bulkArchive(ids: Array<string>, purpose: string): Promise<BulkArchiveTagSchemasResponse> {
  return net.postJSONPromise({url: '/1/api/tag-schemas:bulk-archive', data: {ids, purpose}});
}

interface BulkUnarchiveTagSchemasResponse {
  unarchivedItemIds: Array<string>;
}

export function bulkUnarchive(ids: Array<string>): Promise<BulkUnarchiveTagSchemasResponse> {
  return net.postJSONPromise({url: '/1/api/tag-schemas:bulk-unarchive', data: {ids}});
}

export function star(ids: Array<string>): Promise<StarsResponse> {
  return net.postJSONPromise({url: '/1/api/tag-schemas:star', data: {tagSchemaIds: ids}});
}

export function unstar(ids: Array<string>): Promise<StarsResponse> {
  return net.postJSONPromise({url: '/1/api/tag-schemas:unstar', data: {tagSchemaIds: ids}});
}

export function fetchStarred(): JQueryPromise<StarsResponse> {
  return net.getJSON('/1/api/tag-schemas/starred');
}

export interface CreateTagSchemaParams {
  constraint?: ClientTagSchemaConstraint | null;
  fields: Array<SchemaField | ClientSchemaField>;
  itemType: TagSchemaItemType;
  iconId: BenchlingIconName;
  includeRegistryIdInChips?: boolean;
  isContainable?: boolean;
  labelingStrategies?: Array<RegistryLabelingStrategy>;
  mixtureSchemaConfig?: MixtureSchemaConfig | null;
  name: string;
  prefix: string;
  registryId: string;
  shouldCreateAsOligo?: boolean;
  showResidues?: boolean;
  useOrganizationCollectionAliasForDisplayLabel?: boolean;
  useRandomOrgAlias?: boolean;
  collaborators?: Array<SchemaCollaborator>;
  schemaInterfaceIds: Array<string>;
  implementedStaticSchemaInterfaces: Array<ImplementedStaticSchemaInterface>;
  schemaDescription: string;
  advancedValidationRules?: Array<ValidationRuleUpsertParams>; // Only expect creates during tag schema creation
}

export function create(data: CreateTagSchemaParams): JQueryPromise<TagSchemaGraphQL> {
  const sanitizedData = {...data, fields: sanitizeFields(data.fields)};
  return net.postJSON({url: '/1/api/tag-schemas/', data: sanitizedData});
}

export interface DuplicateTagSchemaParams {
  name: string;
  prefix: string;
  sqlIdentifier?: string;
  copyCollaborators?: boolean;
}

export function duplicate(
  tagSchemaId: string,
  data: DuplicateTagSchemaParams,
  analyticsMetadata?: AnalyticsEventMetadata
): Promise<TagSchemaGraphQL> {
  return net.postJSONPromise({url: `/1/api/tag-schemas/${tagSchemaId}:duplicate`, data, analyticsMetadata});
}

export interface UpdateTagSchemaParams {
  constraint?: ClientTagSchemaConstraint;
  deleteOldSchemaTags?: boolean;
  fields?: Array<SchemaField | ClientSchemaField>;
  itemType?: TagSchemaItemType;
  iconId?: string;
  includeRegistryIdInChips?: boolean;
  isContainable?: boolean;
  labelingStrategies?: Array<RegistryLabelingStrategy>;
  mixtureSchemaConfig?: MixtureSchemaConfig;
  name?: string;
  prefix?: string;
  shouldCreateAsOligo?: boolean;
  showResidues?: boolean;
  useOrganizationCollectionAliasForDisplayLabel?: boolean;
  useRandomOrgAlias?: boolean;
  implementedStaticSchemaInterfaces?: Array<ImplementedStaticSchemaInterface>;
  schemaDescription?: string;
  advancedValidationRules?: Array<AdvancedRuleParams>;
}

export interface UpdateTagSchemaResponse {
  tagSchema: TagSchema;
}

export function update(
  tagSchemaId: string,
  data: UpdateTagSchemaParams
): JQueryPromise<UpdateTagSchemaResponse> {
  return TaskRunner.runTask({
    contentType: 'application/json',
    data: JSON.stringify(data),
    type: 'POST',
    dataType: 'json',
    url: `/1/api/tag-schemas/${tagSchemaId}/actions/update`,
  });
}

export interface SetNameTemplatePartsResponse {
  tagSchema: TagSchema;
}

export function setNameTemplateParts(
  tagSchemaId: string,
  nameTemplateParts: Array<NameTemplatePart>,
  shouldOrderNamePartsBySequence: boolean
): Promise<SetNameTemplatePartsResponse> {
  return net.postJSONPromise({
    url: `/1/api/tag-schemas/${tagSchemaId}/actions/set-name-template`,
    data: {
      nameTemplateParts: nameTemplateParts.map((p) => stripExcessKeys(p)),
      shouldOrderNamePartsBySequence,
    },
  });
}

export interface SetContainerNameTemplateResponse {
  tagSchema: TagSchema;
  nameTemplate: NameTemplate | null;
}

export function setContainerNameTemplateParts(
  tagSchemaId: string,
  nameTemplateParts: Array<NameTemplatePart>,
  dataModsHeader?: DataModificationsHeader
): Promise<SetContainerNameTemplateResponse> {
  return net.postJSONPromise({
    url: `/1/api/tag-schemas/${tagSchemaId}/actions/set-container-name-template`,
    data: {nameTemplateParts: nameTemplateParts.map((p) => stripExcessKeys(p))},
    extraHeaders: dataModsHeader,
  });
}

export type ClientSchemaFieldPath = Omit<SchemaFieldPath, 'id'>;

export interface SetHoistedFieldsResponse {
  tagSchema: TagSchema;
}

export function setHoistedFields(
  tagSchemaId: string,
  hoistedTagSchemaGraphs: Array<SchemaFieldPath | ClientSchemaFieldPath>
): JQueryPromise<SetHoistedFieldsResponse> {
  return net.postJSON({
    url: `/1/api/tag-schemas/${tagSchemaId}/actions/set-hoisted-fields`,
    data: {hoistedTagSchemaGraphs},
  });
}

interface EmbedInsightsResponse {
  tagSchema: AnalyticsDashboardLinkParentGraphQL;
}

export function embedInsights(
  tagSchemaId: string,
  data: AnalyticsDashboardLinkParentSpecRequest
): Promise<EmbedInsightsResponse> {
  return net.postJSONPromise({url: `/1/api/tag-schemas/${tagSchemaId}:embed-insights`, data});
}

interface UpdateTagSchemaCollaboratorsResponse {
  tagSchema: CollaboratorInfoGraphQL;
}

export function updateCollaborators(
  tagSchemaId: string,
  data: UpdateSchemaCollaboratorsRequest
): Promise<UpdateTagSchemaCollaboratorsResponse> {
  return net.patchJSONPromise({url: `/1/api/tag-schemas/${tagSchemaId}/collaborators`, data});
}

export interface ExportSpreadsheetTemplateResponse {
  url: string;
  status: number;
}

export function exportSpreadsheetTemplate(tagSchemaId: string): Promise<ExportSpreadsheetTemplateResponse> {
  return TaskRunner.postJSONPromise({url: `/1/api/tag-schemas/${tagSchemaId}:export-spreadsheet-template`});
}

export function updatedRequiredLinkEntitySchemaFieldPath(
  fieldId: string,
  schemaFieldEdges: Array<SchemaFieldEdge> | null
): Promise<{tagSchema: TagSchema}> {
  return net.postJSONPromise({
    url: `/1/api/tag-schemas/fields:set-entity-schema-field-path`,
    data: {entityLinkFieldId: fieldId, schemaFieldEdges},
  });
}

export function updateTagSchemaValidationRules(
  validationRuleParams: UpdateValidationRulesParams
): Promise<void> {
  const jqPromise = update(validationRuleParams.id, {
    advancedValidationRules: validationRuleParams.advancedValidationRules,
  });
  return new Promise<void>((resolve, reject) => {
    jqPromise
      .done(() => {
        resolve();
      })
      .fail(reject);
  });
}
